import React, {useMemo} from 'react'
import parse from 'html-react-parser'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

import {
    Text,
    Link,
    Box,
    Image,
    Flex,
    useBreakpointValue,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useProducts} from '@salesforce/commerce-sdk-react'

import DisplayPrice from '../../display-price'
import ProductSashes from '../../product-tile/product-sashes'
import {productUrlBuilder} from '../../../utils/url'
import {SearchArrowWhiteIcon} from '../../icons'

const Suggestions = ({suggestions, closeAndNavigate}) => {
    const navigate = useNavigation()
    const isMobile = useBreakpointValue({base: true, md: false})
    const styles = useMultiStyleConfig('Suggestions')
    const productIds = suggestions?.productSuggestions?.map((p) => p.productId).join(',') || ''
    const {data: fullProducts} = useProducts(
        {
            parameters: {
                ids: productIds,
                perPricebook: true,
                allImages: true
            }
        },
        {enabled: productIds?.length > 0}
    )
    const productsWithDetails = useMemo(() => {
        if (!suggestions?.productSuggestions) return []

        return suggestions.productSuggestions.map((suggestion) => ({
            ...suggestion,
            productData:
                fullProducts?.data?.find((p) => p.id === suggestion.productId) ||
                suggestion.productData
        }))
    }, [suggestions, fullProducts])

    const handleOptionClick = (url) => {
        closeAndNavigate(url)
    }

    const handleKeyPress = (event, url) => {
        event.preventDefault()
        closeAndNavigate(url)
    }

    return (
        <Box zIndex={99}>
            {suggestions?.productSuggestions?.length > 0 && suggestions?.totalCount > 0 && (
                <Box
                    textAlign={'end'}
                    padding={'16px'}
                    color={'white'}
                    background={'darkGunmetal'}
                    textTransform={'uppercase'}
                    borderBottom={'1px solid #4a4e58'}
                >
                    <Link
                        as="button"
                        role="option"
                        cursor={'pointer'}
                        onClick={(event) =>
                            handleKeyPress(event, searchUrlBuilder(suggestions?.input))
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                handleKeyPress(event, searchUrlBuilder(suggestions?.input))
                            }
                        }}
                        display="inline-block"
                        textTransform={'uppercase'}
                        _hover={{
                            textDecoration: 'none'
                        }}
                    >
                        <FormattedMessage
                            defaultMessage="See All Results - ( {count} )"
                            id="see_all.results"
                            values={{
                                count: suggestions?.totalCount
                            }}
                        />
                        <Box as="span" ml="10px">
                            <SearchArrowWhiteIcon display="inline" verticalAlign="middle" />
                        </Box>
                    </Link>
                </Box>
            )}

            <Flex
                direction={isMobile ? 'column-reverse' : ''}
                gap={{base: '1em', sm: '0.5em'}}
                background={'darkGunmetal'}
            >
                <Flex gap="0.5em" direction={'column'} flex="1" padding={'25px'}>
                    {/* Did You Mean */}
                    <Box>
                        {suggestions?.categorySearchPhrase?.length > 0 && (
                            <>
                                <Text {...styles.categoryTextStyle}>
                                    <FormattedMessage
                                        defaultMessage="DID YOU MEAN ?"
                                        id="search_suggestions.did_you_mean"
                                    />
                                </Text>
                                {suggestions?.categorySearchPhrase?.map((phrase, idx) => (
                                    <Link
                                        key={idx}
                                        tabIndex={0}
                                        role="option"
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                handleKeyPress(
                                                    event,
                                                    searchUrlBuilder(phrase?.name)
                                                )
                                            }
                                        }}
                                        fontSize="md"
                                        marginTop={0}
                                        variant="menu-link"
                                        color="white"
                                        pb="15px"
                                        display={'block'}
                                        fontWeight="bold"
                                        onClick={() =>
                                            handleOptionClick(searchUrlBuilder(phrase?.name))
                                        }
                                    >
                                        {parse(phrase?.name)}
                                    </Link>
                                ))}
                            </>
                        )}
                    </Box>

                    {/* Category Suggestions */}
                    <Box>
                        {suggestions?.categorySuggestions?.length > 0 && (
                            <>
                                <Text {...styles.categoryTextStyle}>
                                    <FormattedMessage
                                        defaultMessage="CATEGORIES"
                                        id="search_suggestions.categories"
                                    />
                                </Text>
                                {suggestions?.categorySuggestions?.map((suggestion, idx) => (
                                    <Link
                                        key={idx}
                                        tabIndex={0}
                                        role="option"
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                handleKeyPress(event, suggestion?.link)
                                            }
                                        }}
                                        fontSize={'md'}
                                        marginTop={0}
                                        display={'block'}
                                        variant="menu-link"
                                        color={'textColorInvert'}
                                        pb="15px"
                                        fontWeight="normal"
                                        onClick={() => handleOptionClick(suggestion?.link)}
                                    >
                                        {parse(
                                            `<span style="font-weight:bold;">${
                                                suggestion.name
                                            }</span> in ${suggestion?.parentCategoryName || ''}`
                                        )}
                                    </Link>
                                ))}
                            </>
                        )}
                    </Box>

                    {/* Popular Suggestions */}
                    <Box>
                        {suggestions?.popularSuggestions?.length > 0 && (
                            <>
                                <Text {...styles.categoryTextStyle}>
                                    <FormattedMessage
                                        defaultMessage="POPULAR SEARCHES"
                                        id="search_suggestions.popular"
                                    />
                                </Text>
                                {suggestions?.popularSuggestions?.map((suggestion, idx) => (
                                    <Link
                                        key={idx}
                                        tabIndex={0}
                                        role="option"
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                handleKeyPress(
                                                    event,
                                                    searchUrlBuilder(suggestion?.value)
                                                )
                                            }
                                        }}
                                        fontSize={'md'}
                                        marginTop={0}
                                        display={'block'}
                                        variant="menu-link"
                                        color={'textColorInvert'}
                                        pb="15px"
                                        fontWeight={'bold'}
                                        onClick={() =>
                                            navigate(searchUrlBuilder(suggestion?.value))
                                        }
                                    >
                                        {parse(suggestion?.value)}
                                    </Link>
                                ))}
                            </>
                        )}
                    </Box>
                </Flex>

                {productsWithDetails?.length > 0 && (
                    <Box flex="1" padding={'25px'} background={'white'}>
                        <>
                            <Text {...styles.productSugessionTextStyle}>
                                <FormattedMessage
                                    defaultMessage="PRODUCT SUGGESTIONS"
                                    id="search_suggestions.products"
                                />
                            </Text>

                            {productsWithDetails?.map((suggestion, idx) => {
                                const priceData = getPriceData(suggestion.productData)
                                const productDataForUrl = {
                                    productName: suggestion?.productData?.name,
                                    productId: suggestion?.productData?.id
                                }

                                return (
                                    <Flex pb={'10px'} key={idx}>
                                        <Box w="120px" display={{base: 'none', sm: 'inline'}}>
                                            <Link
                                                tabIndex={0}
                                                role="option"
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter') {
                                                        handleKeyPress(
                                                            event,
                                                            productUrlBuilder(productDataForUrl)
                                                        )
                                                    }
                                                }}
                                                fontSize={'md'}
                                                marginTop={0}
                                                variant="menu-link"
                                                color="primaryColor"
                                                display={'block'}
                                                w="120px"
                                                onClick={() =>
                                                    handleOptionClick(
                                                        productUrlBuilder(productDataForUrl)
                                                    )
                                                }
                                            >
                                                <Image
                                                    src={suggestion?.image?.link}
                                                    alt={suggestion?.image?.alt}
                                                    width={'120px'}
                                                    h={'120px'}
                                                    objectFit={'contain'}
                                                />
                                            </Link>
                                        </Box>
                                        <Box ml={{base: '0', sm: '25px'}}>
                                            <Box>
                                                <ProductSashes
                                                    product={suggestion?.productData}
                                                    shownInstock={true}
                                                />
                                            </Box>
                                            <Box>
                                                <Link
                                                    tabIndex={0}
                                                    role="option"
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            handleKeyPress(
                                                                event,
                                                                productUrlBuilder(productDataForUrl)
                                                            )
                                                        }
                                                    }}
                                                    fontSize={'md'}
                                                    marginTop={'5px'}
                                                    variant="menu-link"
                                                    display={'block'}
                                                    lineHeight={'22px'}
                                                    color={'#141519'}
                                                    fontWeight={'normal'}
                                                    onClick={() =>
                                                        handleOptionClick(
                                                            productUrlBuilder(productDataForUrl)
                                                        )
                                                    }
                                                >
                                                    {parse(
                                                        suggestion?.name.replace(
                                                            /<\/?[^>]+(>|$)/g,
                                                            ''
                                                        )
                                                    )}
                                                </Link>
                                            </Box>
                                            <Box>
                                                <Text color="black" fontWeight="normal">
                                                    <Box
                                                        sx={{
                                                            '& .chakra-text': {
                                                                fontSize: '19px !important'
                                                            }
                                                        }}
                                                        mt={'15px'}
                                                    >
                                                        <DisplayPrice
                                                            priceData={priceData}
                                                            currency={suggestion?.currency}
                                                        />
                                                    </Box>
                                                </Text>
                                            </Box>
                                        </Box>
                                    </Flex>
                                )
                            })}
                        </>
                    </Box>
                )}
            </Flex>
        </Box>
    )
}

Suggestions.propTypes = {
    suggestions: PropTypes.object,
    closeAndNavigate: PropTypes.func
}

export default Suggestions
